/**
 * QuestionNode - Node component for 'question' type fields
 * 
 * Displays question text with multiple answer options.
 * Each option has its own source handle for branching connections.
 */

import React, { memo, useState, useCallback, useEffect, useRef } from 'react';
import { Handle, Position, NodeProps } from '@xyflow/react';
import IconProvider from '@/components/ui/IconProvider';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/InputDefault';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/DefaultSelectDropdown';
import { DialogField, DialogOption } from '@/lib/stores/dialogStore';
import { BaseDialogNode } from './BaseDialogNode';
import { DialogFieldNodeData, getFieldTypeIcon, getFieldTypeColor, getSectionName } from './dialogNodeUtils';
import { useDisplayLanguageOptional, resolveFieldDisplayText, resolveOptionDisplayText, resolveFieldEditableText, resolveOptionEditableText, saveFieldTranslation } from '@/contexts/DisplayLanguageContext';
import { useSimplifiedUserData } from '@/providers/SimplifiedUserDataProvider';

export const QuestionNode = memo(({ id, data, selected }: NodeProps<any>) => {
  const nodeData = data as DialogFieldNodeData;
  const field = nodeData.field;
  const { selectedGame } = useSimplifiedUserData();
  const displayLanguageCtx = useDisplayLanguageOptional();
  const displayLanguage = displayLanguageCtx?.displayLanguage ?? null;
  const effectiveGameId = selectedGame?.id;
  const [isEditing, setIsEditing] = useState(false);
  const [editedType, setEditedType] = useState(field.type);
  const [editedText, setEditedText] = useState(field.text);
  const [editedOptions, setEditedOptions] = useState<DialogOption[]>(field.options || []);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const prevSelectedRef = useRef(selected);

  // Attach native event listeners to prevent dragging on all inputs and textarea
  useEffect(() => {
    const content = contentRef.current;
    if (!content || !isEditing) return;

    const handlePointerDown = (e: PointerEvent) => {
      // Only prevent if target is an input, select, or textarea element
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'SELECT' || target.tagName === 'TEXTAREA' || 
          target.closest('input') || target.closest('select') || target.closest('textarea') ||
          target.closest('[role="combobox"]')) {
        e.stopImmediatePropagation();
      }
    };

    const handleMouseDown = (e: MouseEvent) => {
      // Only prevent if target is an input, select, or textarea element
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'SELECT' || target.tagName === 'TEXTAREA' || 
          target.closest('input') || target.closest('select') || target.closest('textarea') ||
          target.closest('[role="combobox"]')) {
        e.stopImmediatePropagation();
      }
    };

    // Use capture phase to run before React Flow's listeners
    content.addEventListener('pointerdown', handlePointerDown, { capture: true });
    content.addEventListener('mousedown', handleMouseDown, { capture: true });
    
    return () => {
      content.removeEventListener('pointerdown', handlePointerDown, { capture: true });
      content.removeEventListener('mousedown', handleMouseDown, { capture: true });
    };
  }, [isEditing]);

  // Sync local state with prop changes when not editing
  useEffect(() => {
    if (!isEditing) {
      setEditedType(field.type);
      setEditedText(field.text);
      setEditedOptions(field.options || []);
    }
  }, [field, isEditing]);

  // Are we editing a translation (non-source language + TMS-linked field)?
  const isTranslationMode = displayLanguage !== null && !!field.string_id && !!field.translatable;

  const handleSaveChanges = useCallback(() => {
    if (isTranslationMode && effectiveGameId && displayLanguage) {
      // Translation mode: persist text translations to TMS, don't overwrite source
      if (field.string_id) {
        saveFieldTranslation(field.string_id, displayLanguage, editedText, effectiveGameId);
      }
      // Save option translations to TMS
      if (editedType === 'question') {
        editedOptions.forEach((opt) => {
          if (opt.string_id) {
            saveFieldTranslation(opt.string_id, displayLanguage, opt.option_text, effectiveGameId);
          }
        });
      }
      // Still allow type changes to be saved
      if (editedType !== field.type && nodeData.onFieldUpdate) {
        nodeData.onFieldUpdate(nodeData.nodeIndex, nodeData.fieldIndex, { type: editedType });
      }
    } else if (nodeData.onFieldUpdate) {
      // Source mode: update field data as before
      const updates: Partial<DialogField> = {
        type: editedType,
        text: editedText,
      };
      
      // Only include options if it's a question type
      if (editedType === 'question') {
        updates.options = editedOptions;
      }
      
      nodeData.onFieldUpdate(nodeData.nodeIndex, nodeData.fieldIndex, updates);
    }
    setIsEditing(false);
  }, [editedType, editedText, editedOptions, nodeData, isTranslationMode, field.string_id, field.type, displayLanguage, effectiveGameId]);

  // Auto-save when node loses selection (clicking outside)
  useEffect(() => {
    // Only auto-save if we're transitioning from selected to unselected while editing
    if (prevSelectedRef.current && !selected && isEditing) {
      handleSaveChanges();
    }
    // Update ref for next render
    prevSelectedRef.current = selected;
  }, [selected, isEditing, handleSaveChanges]);

  const handleEditToggle = useCallback(() => {
    if (isEditing) {
      handleSaveChanges();
    } else {
      setIsEditing(true);
      setEditedType(field.type);
      setEditedText(resolveFieldEditableText(field, displayLanguage, effectiveGameId));
      // Resolve option texts so translations are shown in edit mode
      const resolvedOptions = (field.options || []).map(opt => ({
        ...opt,
        option_text: resolveOptionEditableText(opt, displayLanguage, effectiveGameId),
      }));
      setEditedOptions(resolvedOptions);
    }
  }, [isEditing, handleSaveChanges, field, displayLanguage, effectiveGameId]);

  const handleAddOption = useCallback(() => {
    const newOption: DialogOption = {
      id: `temp-${Date.now()}`,
      option_text: 'New Option',
      translatable: true,
      order_index: editedOptions.length,
    };
    setEditedOptions([...editedOptions, newOption]);
  }, [editedOptions]);

  const handleUpdateOption = useCallback((index: number, text: string) => {
    const updated = [...editedOptions];
    updated[index] = { ...updated[index], option_text: text };
    setEditedOptions(updated);
  }, [editedOptions]);

  const handleRemoveOption = useCallback((index: number) => {
    setEditedOptions(editedOptions.filter((_, i) => i !== index));
  }, [editedOptions]);

  const colors = getFieldTypeColor(isEditing ? editedType : field.type);
  const hasOptions = field.options && field.options.length > 0;

  // Header content for edit mode (with type selector)
  const headerContent = isEditing ? (
    <>
      <IconProvider iconstring={getFieldTypeIcon(editedType)} size={14} className={colors.text} />
      <Select value={editedType} onValueChange={(value: any) => setEditedType(value)}>
        <SelectTrigger className="h-6 text-xs flex-1 bg-white noDrag">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="dialog">Dialog</SelectItem>
          <SelectItem value="question">Question</SelectItem>
          <SelectItem value="context">Context</SelectItem>
          <SelectItem value="end-dialog">End Dialog</SelectItem>
        </SelectContent>
      </Select>
    </>
  ) : undefined;

  return (
    <BaseDialogNode
      id={id}
      data={nodeData}
      selected={selected}
      isEditing={isEditing}
      onEditToggle={handleEditToggle}
      headerContent={headerContent}
    >
      <div ref={contentRef}>
        {/* Question text */}
        {isEditing ? (
        <Textarea
          ref={textareaRef}
          value={editedText}
          onChange={(e) => setEditedText(e.target.value)}
          className="text-sm mb-2 min-h-[60px] noDrag"
          placeholder="Enter question text..."
        />
      ) : (
        <div className="text-sm mb-2 line-clamp-3">
          {resolveFieldDisplayText(field, displayLanguage, effectiveGameId) || '(empty)'}
        </div>
      )}

      {/* Options editing */}
      {isEditing && editedType === 'question' && (
        <div className="mt-2 pt-2 border-t space-y-1">
          <div className="flex items-center justify-between mb-1">
            <div className="text-xs font-semibold text-muted-foreground">Options:</div>
            <Button
              onClick={handleAddOption}
              size="sm"
              variant="ghost"
              className="h-6 px-2 text-xs"
            >
              <IconProvider iconstring="fa-plus" size={10} className="mr-1" />
              Add
            </Button>
          </div>
          {editedOptions.map((option, idx) => (
            <div 
              key={option.id || idx}
              className="flex items-center gap-2 text-xs p-1.5 bg-gray-50 rounded"
            >
              <Input
                value={option.option_text}
                onChange={(e) => handleUpdateOption(idx, e.target.value)}
                className="h-7 text-xs flex-1 noDrag"
                placeholder={`Option ${idx + 1}`}
              />
              <button
                onClick={() => handleRemoveOption(idx)}
                className="hover:bg-red-100 p-1 rounded transition-colors"
              >
                <IconProvider iconstring="fa-trash" size={10} className="text-red-600" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Options display (view mode) */}
      {!isEditing && hasOptions && (
        <div className="mt-2 pt-2 border-t space-y-1">
          <div className="text-xs font-semibold text-muted-foreground mb-1">Options:</div>
          {field.options!.map((option, idx) => {
            const isExternalJump = !!option.next_dialog;
            const isInternalJump = !!option.next_section;
            const hasVariableAction = !!option.variableAction;
            const hasVisibilityCondition = !!option.visibilityCondition;
            
            return (
              <div 
                key={option.id || idx}
                className={`flex items-start gap-2 text-xs p-1.5 rounded relative transition-colors ${
                  isExternalJump 
                    ? 'bg-orange-50 hover:bg-orange-100 border border-orange-200' 
                    : isInternalJump
                    ? 'bg-purple-50 hover:bg-purple-100'
                    : 'bg-gray-50 hover:bg-gray-100'
                }`}
              >
                <IconProvider 
                  iconstring={isExternalJump ? "fa-arrow-up-right-from-square" : "fa-arrow-right"} 
                  size={10} 
                  className={`mt-0.5 flex-shrink-0 ${isExternalJump ? 'text-orange-600' : ''}`}
                />
                <div className="flex-1 space-y-0.5">
                  <div className="truncate">{resolveOptionDisplayText(option, displayLanguage, effectiveGameId)}</div>
                  {/* Variable action badge */}
                  {hasVariableAction && (
                    <div className="flex items-center gap-1 text-[10px] text-purple-700">
                      <IconProvider iconstring="fa-code" size={8} />
                      <span className="font-mono">
                        {option.variableAction!.variable} {option.variableAction!.operator === 'set' ? '=' : option.variableAction!.operator === 'increment' ? '+=' : '-='} {String(option.variableAction!.value)}
                      </span>
                    </div>
                  )}
                  {/* Visibility condition badge */}
                  {hasVisibilityCondition && (
                    <div className="flex items-center gap-1 text-[10px] text-blue-700">
                      <IconProvider iconstring="fa-eye" size={8} />
                      <span className="font-mono">
                        if {option.visibilityCondition!.variable} {option.visibilityCondition!.operator} {String(option.visibilityCondition!.value)}
                      </span>
                    </div>
                  )}
                </div>
                
                {/* Visual indicator for external jumps */}
                {isExternalJump && (
                  <div className="flex items-center gap-0.5 px-1 py-0.5 bg-orange-100 rounded text-[10px] text-orange-700">
                    <IconProvider iconstring="fa-external-link" size={8} />
                  </div>
                )}
                
                {/* Handle for each option */}
                <Handle 
                  type="source" 
                  position={Position.Right}
                  id={`option-${idx}`}
                  className={`!absolute !right-[-6px] !top-1/2 !-translate-y-1/2 w-3 h-3 transition-colors ${
                    isExternalJump 
                      ? '!bg-orange-500 hover:!bg-orange-600' 
                      : '!bg-purple-500 hover:!bg-purple-600'
                  }`}
                  style={{ position: 'absolute' }}
                />
              </div>
            );
          })}
        </div>
      )}

      {/* Metadata badges */}
      {!isEditing && field.translatable && (
        <div className="flex items-center gap-2 mt-2 text-xs">
          <div className="flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 rounded">
            <IconProvider iconstring="fa-language" size={10} />
            <span>Translatable</span>
          </div>
        </div>
      )}
      </div>
    </BaseDialogNode>
  );
});

QuestionNode.displayName = 'QuestionNode';

export default QuestionNode;
